import type { ScanRequestRow } from "../../../../api/roleProvisioning.service.js";

/**
 * Client-side half of the Role Provisioning CSV contract (migration-plan.md §3).
 *
 * Pure functions, split in two stages because the operator can re-map columns
 * after the file is on screen: `parseCsvText` reads the file's STRUCTURE
 * (encoding, delimiter, preamble, header, cells) and `buildRows` applies a
 * column mapping to produce the rows the scan will receive, plus the rows it
 * never will — `invalid_row` and `duplicate_in_file` are decided here and only
 * ever shown in the preview's "skipped" group.
 *
 * Forgiving about how the file was produced, because both real files were
 * exported by CS from Excel: cp1252 bytes, `;` as the delimiter, a few title
 * lines before the header, CRLF, a BOM. Strict about the rows themselves.
 */

export const ROW_LIMIT = 5000;

export type Delimiter = "," | ";" | "\t";

export type ClientOutcome = "invalid_row" | "duplicate_in_file";

export type Encoding = "utf-8" | "windows-1252";

/** One physical line of the file after the header, split into cells. */
export type CsvRecord = {
  /** 1-indexed line in the source file, counting preamble and header. */
  lineNumber: number;
  cells: string[];
};

export type ColumnMapping = {
  email: number | null;
  firmRef: number | null;
};

export type ParsedCsv = {
  delimiter: Delimiter;
  headers: string[];
  headerLineNumber: number;
  records: CsvRecord[];
  /** Column mapping guessed from the header names; the operator can override it. */
  guess: ColumnMapping;
  /** File-level problems. A file with any error cannot be scanned. */
  errors: string[];
};

/** A file row the scan will never see, with the reason it was held back. */
export type ProblemRow = {
  lineNumber: number;
  email: string;
  outcome: ClientOutcome;
  detail: string;
};

export type BuiltRows = {
  rows: ScanRequestRow[];
  problems: ProblemRow[];
};

export const EMAIL_HEADER = /email/i;
export const FIRM_REF_HEADER = /firm_?ref/i;

/**
 * Decodes the raw bytes: strict UTF-8 first, and when that fails (an invalid
 * sequence — cp1252's `é` is a lone 0xE9) the Windows code page Excel writes.
 * Returns which one was used so the page can say so.
 */
export function decodeCsvBytes(bytes: Uint8Array): { text: string; encoding: Encoding } {
  try {
    return { text: new TextDecoder("utf-8", { fatal: true }).decode(bytes), encoding: "utf-8" };
  } catch {
    return { text: new TextDecoder("windows-1252").decode(bytes), encoding: "windows-1252" };
  }
}

/** The header line is the first one that names an email column. */
function detectDelimiter(header: string): Delimiter {
  const count = (delimiter: Delimiter) => header.split(delimiter).length - 1;
  const candidates: Delimiter[] = [",", ";", "\t"];
  let best: Delimiter = ",";
  for (const candidate of candidates) {
    if (count(candidate) > count(best)) {
      best = candidate;
    }
  }
  return best;
}

/**
 * Splits one line into cells, honouring double quotes: a quoted cell may
 * contain the delimiter (firm names carry commas) and `""` unescapes to a
 * literal quote. Same rules as the sibling backfill tools' parsers.
 */
export function splitFields(line: string, delimiter: Delimiter): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (inQuotes) {
      if (character === '"') {
        if (line[index + 1] === '"') {
          current += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += character;
      }
    } else if (character === '"' && current === "") {
      inQuotes = true;
    } else if (character === delimiter) {
      fields.push(current);
      current = "";
    } else {
      current += character;
    }
  }
  fields.push(current);
  return fields;
}

/** First header matching each pattern wins; null when nothing matches. */
export function guessColumns(headers: string[]): ColumnMapping {
  const indexOf = (pattern: RegExp) => {
    const index = headers.findIndex(header => pattern.test(header));
    return index === -1 ? null : index;
  };
  return { email: indexOf(EMAIL_HEADER), firmRef: indexOf(FIRM_REF_HEADER) };
}

/**
 * Reads the file's structure. Strips a BOM, normalises line endings, skips
 * preamble lines until one names an email column, detects the delimiter from
 * that header line, and splits everything after it into cells. Blank lines are
 * dropped without shifting the line numbers of what follows.
 */
export function parseCsvText(text: string): ParsedCsv {
  const lines = text.replace(/^﻿/, "").replace(/\r\n?/g, "\n").split("\n");
  const headerIndex = lines.findIndex(line => EMAIL_HEADER.test(line));

  if (headerIndex === -1) {
    return {
      delimiter: ",",
      headers: [],
      headerLineNumber: 0,
      records: [],
      guess: { email: null, firmRef: null },
      errors: ["No header row with an email column found. Expected a header containing 'email'."],
    };
  }

  const delimiter = detectDelimiter(lines[headerIndex] ?? "");
  const headers = splitFields(lines[headerIndex] ?? "", delimiter).map(header => header.trim());
  const records: CsvRecord[] = [];
  for (let index = headerIndex + 1; index < lines.length; index += 1) {
    const line = lines[index] ?? "";
    if (line.trim() === "") {
      continue;
    }
    records.push({ lineNumber: index + 1, cells: splitFields(line, delimiter) });
  }

  const errors: string[] = [];
  if (records.length === 0) {
    errors.push("That file has a header but no rows.");
  }
  if (records.length > ROW_LIMIT) {
    errors.push(
      `That file has ${records.length.toLocaleString()} rows; the limit is ` +
        `${ROW_LIMIT.toLocaleString()} per file. Split it and run each part.`,
    );
  }

  return {
    delimiter,
    headers,
    headerLineNumber: headerIndex + 1,
    records,
    guess: guessColumns(headers),
    errors,
  };
}

/**
 * Applies a column mapping to the parsed records. Emails are trimmed and
 * lower-cased; a cell without `@` or a firm_ref that is not a positive integer
 * is `invalid_row`; a repeated email keeps its first occurrence and marks the
 * rest `duplicate_in_file`. Nothing here reaches the server.
 */
export function buildRows(parsed: ParsedCsv, mapping: ColumnMapping): BuiltRows {
  const rows: ScanRequestRow[] = [];
  const problems: ProblemRow[] = [];
  if (mapping.email === null) {
    return { rows, problems };
  }

  const firstLineByEmail = new Map<string, number>();
  for (const { lineNumber, cells } of parsed.records) {
    const email = (cells[mapping.email] ?? "").trim().toLowerCase();
    if (!email.includes("@")) {
      problems.push({
        lineNumber,
        email,
        outcome: "invalid_row",
        detail: email === "" ? "email cell is empty" : `'${email}' is not an email address`,
      });
      continue;
    }

    let firmRef: string | null = null;
    if (mapping.firmRef !== null) {
      const raw = (cells[mapping.firmRef] ?? "").trim();
      if (raw !== "") {
        if (!/^\d+$/.test(raw) || Number(raw) === 0) {
          problems.push({
            lineNumber,
            email,
            outcome: "invalid_row",
            detail: `firm_ref '${raw}' is not a positive integer`,
          });
          continue;
        }
        firmRef = raw;
      }
    }

    const firstLine = firstLineByEmail.get(email);
    if (firstLine !== undefined) {
      problems.push({
        lineNumber,
        email,
        outcome: "duplicate_in_file",
        detail: `same email first seen on line ${firstLine}`,
      });
      continue;
    }
    firstLineByEmail.set(email, lineNumber);
    rows.push({ lineNumber, email, firmRef });
  }

  return { rows, problems };
}
